/**
 * TemplateDetailModal -- read-only preview of an interview template.
 *
 * Used from the Recruiter Hub template grid so users can inspect what's
 * actually inside a Cevee-curated (public) or company-owned template
 * before deciding whether to invite a candidate with it.
 *
 * For DE/EN family pairs (family_key match) we show a small language
 * toggle so the recruiter can flip between the two versions without
 * having to close the modal.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  groupQuestionsByTask,
  pickSampleTaskSteps,
  pickSampleInterview,
} from '../../lib/interviewPoolTasks';
import {
  X,
  Clock,
  ListChecks,
  MessageSquare,
  Code,
  PencilLine,
  Calculator,
  FileText,
  ShieldCheck,
  Shuffle,
  Send,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Layers,
} from 'lucide-react';

import {
  cleanTemplateTitle,
  normalizeEvaluationCriteria,
  sanitizeInterviewCopy,
  type InterviewTemplate,
  type InterviewQuestion,
  type Persona,
} from '../../api/interview';
import { useLanguage } from '../../contexts/LanguageContext';

interface TemplateDetailModalProps {
  isOpen: boolean;
  /** The whole list - used to find DE/EN siblings of the same family. */
  templates: InterviewTemplate[];
  initial: InterviewTemplate | null;
  personas: Persona[];
  onClose: () => void;
  /**
   * Optional: when present, footer shows a "Use as starting point"
   * primary action. Hub passes a handler that closes this modal and
   * opens the TemplateBuilderModal pre-filled with the active sibling.
   */
  onUseAsTemplate?: (
    tpl: InterviewTemplate,
    opts?: {
      sampleSteps?: NonNullable<InterviewTemplate['questions']>;
      language?: 'de' | 'en';
    },
  ) => void;
  /**
   * Locks the "Use as template" CTA (e.g. while DPA is missing).
   */
  canUseAsTemplate?: boolean;
  /**
   * Optional: when present, footer shows a primary "Invite" action.
   * Hub passes a handler that closes this modal and opens the
   * InviteCreateModal pre-selected to this template's family.
   */
  onInvite?: (tpl: InterviewTemplate) => void;
  /**
   * Locks the "Invite" CTA (gated by DPA on the recruiter side).
   */
  canInvite?: boolean;
  /** Fetch full template (incl. questions) when user switches DE/EN sibling. */
  onNeedTemplate?: (templateId: string) => void;
  /**
   * Mock / self-serve: level + duration are chosen at session start, not
   * fixed on the template row.
   */
  sessionConfigurable?: boolean;
  /**
   * Mock: opens the session config flow (level, duration, consent) for
   * this template. Shown when sessionConfigurable and not recruiter invite.
   */
  onConfigureSimulation?: (tpl: InterviewTemplate) => void;
}

const TemplateDetailModal: React.FC<TemplateDetailModalProps> = ({
  isOpen,
  templates,
  initial,
  personas,
  onClose,
  onUseAsTemplate,
  canUseAsTemplate = true,
  onInvite,
  canInvite = true,
  onNeedTemplate,
  sessionConfigurable = false,
  onConfigureSimulation,
}) => {
  const { language: lang } = useLanguage();

  // Resolve siblings: anything sharing family_key with `initial` (when set)
  const siblings = useMemo(() => {
    if (!initial) return [] as InterviewTemplate[];
    if (!initial.family_key) return [initial];
    return templates.filter((t) => t.family_key === initial.family_key);
  }, [initial, templates]);

  const langsAvailable = useMemo(
    () =>
      Array.from(new Set(siblings.map((t) => t.language)))
        .sort((a, b) => (a === 'de' ? -1 : b === 'de' ? 1 : a.localeCompare(b))),
    [siblings],
  );

  const [activeLang, setActiveLang] = useState<string>(initial?.language || 'en');
  useEffect(() => {
    if (initial?.language) setActiveLang(initial.language);
  }, [initial?.id, initial?.language]);
  // Long template descriptions get clamped to 2 lines in the header so
  // the modal doesn't grow vertically before the user has even reached
  // the meaty content. A "Mehr lesen / Read more" toggle expands the
  // full text inline. Reset whenever the template/lang changes.
  const [descExpanded, setDescExpanded] = useState<boolean>(false);
  // Seed for the random sample-questions preview shown on Practice
  // pools. Bumping this re-shuffles the displayed sample. We intentionally
  // do NOT pre-show pool questions for hiring pools (anti-cheat); only
  // practice pools surface a teaser, since their questions are disjoint
  // from the matching hiring pool by design.
  const [sampleSeed, setSampleSeed] = useState<number>(() => Math.floor(Math.random() * 1e9));
  // Recruiter-only: index for paging through the FULL pool (trust signal).
  const [browseIdx, setBrowseIdx] = useState<number>(0);
  // Sample section view: a full example interview vs. browsing the whole pool.
  const [poolView, setPoolView] = useState<'example' | 'pool'>('example');
  useEffect(() => {
    setDescExpanded(false);
    setSampleSeed(Math.floor(Math.random() * 1e9));
    setBrowseIdx(0);
    setPoolView('example');
  }, [initial?.id, activeLang]);

  // Pick the template to render - first sibling matching activeLang, else
  // fall back to whatever we were opened with.
  const active = useMemo(() => {
    return (
      siblings.find((t) => t.language === activeLang) || initial || siblings[0] || null
    );
  }, [siblings, activeLang, initial]);

  // Practice-pool teaser questions: deterministic 5-of-N sample driven by
  // sampleSeed. Computed BEFORE any early return so rules-of-hooks is
  // never violated when the modal opens or closes.
  // One full sample interview (all steps of one pool task).
  const poolSampleTaskSteps = useMemo(() => {
    if (!active) return [] as NonNullable<typeof active>['questions'];
    const isPoolLocal = !!active.sampling_strategy;
    const isPracticeOnlyPool =
      isPoolLocal && !!active.available_for_practice && !active.available_for_recruiter;
    // Mock: practice pools only (candidates must not preview hiring-pool cases).
    // Recruiter hub: any pool they open (practice or hiring) gets a teaser.
    const isRecruiterPreview = !!(onUseAsTemplate || onInvite);
    const canShowSample =
      isPoolLocal && (isPracticeOnlyPool || isRecruiterPreview);
    const all = active.questions || [];
    if (!canShowSample || all.length === 0) return [];
    return pickSampleTaskSteps(all, sampleSeed);
  }, [active, sampleSeed, onUseAsTemplate, onInvite]);

  // Recruiter-only: ALL pool tasks (each = one case's steps), stably sorted
  // by area then task id so prev/next paging is deterministic.
  const poolTasksAll = useMemo(() => {
    if (!active || !active.sampling_strategy) return [] as NonNullable<typeof active>['questions'][];
    const areaOf = (steps: NonNullable<typeof active>['questions']) =>
      ((steps[0]?.tags || []).find(
        (t): t is string => typeof t === 'string' && t.startsWith('area:'),
      ) || '').toLowerCase();
    return Array.from(groupQuestionsByTask(active.questions || []).values()).sort((a, b) => {
      const aa = areaOf(a);
      const ab = areaOf(b);
      if (aa !== ab) return aa.localeCompare(ab);
      return String(a[0]?.id || '').localeCompare(String(b[0]?.id || ''));
    });
  }, [active]);

  // A full example interview: `total` cases assembled the way the live sampler
  // would (distinct areas), so the preview matches multi-case screens too.
  const exampleInterviewCases = useMemo(() => {
    if (!active || !active.sampling_strategy) return [] as NonNullable<typeof active>['questions'][];
    const strat = active.sampling_strategy;
    const total = strat?.total ?? 1;
    const distinct = strat?.distinct_areas !== false;
    const all = active.questions || [];
    if (all.length === 0) return [];
    return pickSampleInterview(all, total, distinct, sampleSeed);
  }, [active, sampleSeed]);

  useEffect(() => {
    if (!isOpen || !onNeedTemplate || !active) return;
    if ((active.questions || []).length > 0) return;
    onNeedTemplate(active.id);
  }, [isOpen, active?.id, active?.questions?.length, onNeedTemplate]);

  if (!isOpen || !active) return null;

  const persona = personas.find((p) => p.id === active.persona_id);
  const cleanTitle = cleanTemplateTitle(active.title);
  const evalCriteria = normalizeEvaluationCriteria(active.evaluation_criteria);
  const totalWeight = evalCriteria.reduce(
    (s, c) => s + (Number(c.weight) || 0),
    0,
  );

  // Pool-mode detection: when sampling_strategy is present the template
  // is a question POOL and the AI picks a per-session subset. We render
  // a "pool" summary card instead of the full question list to avoid
  // misleading the candidate into thinking they'll see all N questions.
  const isPool = !!active.sampling_strategy;
  const poolStrategy = active.sampling_strategy || null;
  const poolTotal = (poolStrategy?.total ?? 2);
  const poolDays = (poolStrategy?.exclude_seen_in_last_days ?? 30);
  const stepsFromSample = poolSampleTaskSteps.length;
  const stepsFromApi = active.steps_per_session;
  const sessionStepCount = isPool
    ? (stepsFromSample || stepsFromApi || 5)
    : (active.questions || []).length;
  const formatArea = (a: string) =>
    a
      .split('_')
      .map((w) => w[0].toUpperCase() + w.slice(1))
      .join(' ');
  // Distinct topic areas in the pool (names only, no per-area counts).
  const poolAreas: string[] = (() => {
    if (!isPool) return [];
    const areas = new Set<string>();
    for (const steps of Array.from(groupQuestionsByTask(active.questions || []).values())) {
      const label = poolAreaLabel(steps[0]?.tags, formatArea);
      if (label) areas.add(label);
    }
    return Array.from(areas).sort((a, b) => a.localeCompare(b));
  })();
  const CAREER_LEVELS = new Set(['student', 'junior', 'mid', 'senior', 'staff']);
  const templateLevel =
    active && CAREER_LEVELS.has((active.difficulty || '').toLowerCase())
      ? (active.difficulty || '').toLowerCase()
      : null;

  const isPracticeOnlyPool =
    isPool && !!active.available_for_practice && !active.available_for_recruiter;
  const isRecruiterPoolPreview = !!(onUseAsTemplate || onInvite);
  /** Pool templates: mock picks level+duration at start; recruiter sets them in the invite. */
  const isCeveeOfficial = !!active?.is_cevee_official;
  const isSessionConfigurable = sessionConfigurable && !isCeveeOfficial;
  const configAtInvite = isSessionConfigurable && !!onInvite;
  const poolTaskCount = poolTasksAll.length;
  const clampedIdx = Math.min(Math.max(0, browseIdx), Math.max(0, poolTaskCount - 1));
  // Full-pool paging is recruiter-only; candidates/mock can't browse all cases.
  const canBrowsePool = isRecruiterPoolPreview && poolTaskCount > 1;
  const effectiveView: 'example' | 'pool' = canBrowsePool ? poolView : 'example';
  const showPoolSample =
    isPool &&
    (isPracticeOnlyPool || isRecruiterPoolPreview) &&
    (effectiveView === 'pool'
      ? (poolTasksAll[clampedIdx] || []).length > 0
      : exampleInterviewCases.length > 0);
  const POOL_SAMPLE_SIZE = 5;

  // Heuristic: only show the toggle when the description is long
  // enough that line-clamp would actually hide content. Below this
  // threshold the toggle would be a no-op and just add visual noise.
  const descTruncatable = !!active.description && active.description.length > 140;

  const labels = {
    badge:           lang === 'de' ? 'Template-Vorschau' : 'Template preview',
    readMore:        lang === 'de' ? 'Mehr lesen' : 'Read more',
    readLess:        lang === 'de' ? 'Weniger' : 'Show less',
    duration:        lang === 'de' ? 'Dauer' : 'Duration',
    minutes:         lang === 'de' ? 'Min.' : 'min',
    level:           lang === 'de' ? 'Level' : 'Level',
    category:        lang === 'de' ? 'Kategorie' : 'Category',
    interviewer:     lang === 'de' ? 'AI-Interviewerin' : 'AI interviewer',
    languageLabel:   lang === 'de' ? 'Sprache' : 'Language',
    panelsTitle:     lang === 'de' ? 'Live-Panels' : 'Live panels',
    panelCoding:     lang === 'de' ? 'Live-Coding-Editor' : 'Live coding editor',
    panelWB:         lang === 'de' ? 'Whiteboard' : 'Whiteboard',
    panelNum:        lang === 'de' ? 'Zahlen-Pane (Mini-Spreadsheet)' : 'Numbers pane (mini spreadsheet)',
    panelNotepad:    lang === 'de' ? 'Notizblock (Post-it)' : 'Notepad (post-it)',
    panelNone:       lang === 'de' ? 'Nur Voice - keine zusätzlichen Panels' : 'Voice only - no extra panels',
    criteriaTitle:   lang === 'de' ? 'Bewertungskriterien' : 'Evaluation criteria',
    weightLabel:     lang === 'de' ? 'Gewicht' : 'Weight',
    sum:             lang === 'de' ? 'Summe' : 'Sum',
    questionsTitle:  onUseAsTemplate
      ? (lang === 'de' ? 'Fragen' : 'Questions')
      : (lang === 'de' ? 'Beispiel-Fragen' : 'Example questions'),
    questionsHelp:   onUseAsTemplate
      ? (lang === 'de'
        ? 'Diese Fragen stellt die AI als Leitfaden. Sie passt sich an die Antworten an und stellt eigene Follow-ups.'
        : 'These questions guide the AI. It adapts to the candidate\'s answers and asks its own follow-ups.')
      : (lang === 'de'
        ? 'Beispiele zur Orientierung. Die KI passt Fragen, Tonalität, Tiefe und Pacing an dein gewähltes Level und die gewählte Dauer an. Du musst nicht jede Frage exakt erwarten.'
        : 'Examples for orientation. The AI adapts questions, tone, depth, and pacing to your chosen level and duration. Don\'t expect every question word-for-word.'),
    criteriaHelp:    lang === 'de'
      ? 'Die Kriterien-Texte sind fix, aber die Bewertungs-Latte verschiebt sich automatisch mit deinem Level (Student milder, Senior strenger).'
      : 'Criteria texts are fixed, but the grading bar shifts automatically with your level (lenient for student, strict for senior).',
    poolTitle:       lang === 'de' ? 'Cevee Frage-Pool' : 'Cevee question pool',
    poolBadge:       lang === 'de' ? 'Pool' : 'Pool',
    poolPerSession:  poolTotal > 1
      ? (lang === 'de'
        ? `Pro Interview: ${poolTotal} Cases aus dem Pool (verschiedene Bereiche), je ca. ${sessionStepCount} Schritte (Setup → Live → Abschluss).`
        : `Per interview: ${poolTotal} cases from the pool (distinct areas), ~${sessionStepCount} steps each (setup → live → close).`)
      : (lang === 'de'
        ? `Pro Interview: 1 Case aus dem Pool mit ca. ${sessionStepCount} Schritten (Setup → Live → Abschluss).`
        : `Per interview: 1 case from the pool with ~${sessionStepCount} steps (setup → live → close).`),
    poolDistinct:    lang === 'de'
      ? 'Aus verschiedenen Themenbereichen, damit keine Session sich wiederholt.'
      : 'Across distinct topic areas so no two sessions feel the same.',
    poolDifficulty:  isCeveeOfficial
      ? (lang === 'de'
        ? `Level ${templateLevel || active.difficulty} und Dauer ${active.duration_minutes} Min. sind standardisiert (fix).`
        : `Level ${templateLevel || active.difficulty} and duration ${active.duration_minutes} min are standardised (fixed).`)
      : isSessionConfigurable
        ? (configAtInvite
          ? (lang === 'de'
            ? 'Level und Dauer (30 / 45 / 60 Min.) legst du in der Einladung fest, nicht hier am Template.'
            : 'Set level and duration (30 / 45 / 60 min) in the invite, not on this template.')
          : (lang === 'de'
            ? 'Level + Dauer wählst du beim Start (Student / Junior / Mid / Senior, 30 / 45 / 60 Min).'
            : 'You pick level + duration when starting (Student / Junior / Mid / Senior, 30 / 45 / 60 min).'))
        : (lang === 'de'
          ? `Voreinstellung: ${active.duration_minutes} Min. pro Interview (vom Template).`
          : `Default: ${active.duration_minutes} min per interview (from template).`),
    poolSampleRecruiterNote: lang === 'de'
      ? 'Vorschau für Recruiter:innen. Kandidat:innen im echten Interview sehen einen anderen Case aus diesem Pool.'
      : 'Recruiter preview only. Candidates in a live interview get a different case from this pool.',
    poolAntiReplay:  lang === 'de'
      ? `Wir versuchen, dir innerhalb von ${poolDays} Tagen keine Frage zweimal zu zeigen.`
      : `We try not to show you the same question twice within ${poolDays} days.`,
    poolAreasTitle:  lang === 'de' ? 'Themenbereiche im Pool' : 'Areas in the pool',
    poolHiddenNote:  lang === 'de'
      ? 'Die genauen Fragen siehst du erst im Interview, wie in einem echten Gespräch.'
      : 'You see the actual questions only inside the interview, just like in a real conversation.',
    poolSampleTitle: poolTotal > 1
      ? (lang === 'de'
        ? `Beispiel-Case (1 von ${poolTotal})`
        : `Sample case (1 of ${poolTotal})`)
      : (lang === 'de'
        ? 'Beispiel-Interview (ein Case)'
        : 'Sample interview (one case)'),
    poolSampleHelp:  poolTotal > 1
      ? (lang === 'de'
        ? `Ein zufälliger Case aus dem Pool mit allen Schritten, nur zur Orientierung. Live zieht die KI ${poolTotal} verschiedene Cases; Level und Dauer passen sich an.`
        : `One random case from the pool with all steps, orientation only. Live, the AI draws ${poolTotal} different cases; level and duration adapt.`)
      : (lang === 'de'
        ? 'Ein zufälliger Case mit allen Schritten, nur zur Orientierung. Live wählt die KI einen anderen Case; Level und Dauer passen sich an.'
        : 'One random case with all steps, orientation only. Live, the AI picks a different case; level and duration adapt.'),
    poolSampleReshuffle: lang === 'de' ? 'Andere Auswahl' : 'Reshuffle',
    poolSize: lang === 'de'
      ? `Insgesamt ${poolTaskCount} Cases im Pool.`
      : `${poolTaskCount} cases in the pool in total.`,
    poolBrowseTitle: lang === 'de' ? 'Alle Cases im Pool' : 'All cases in the pool',
    poolBrowseHelp: lang === 'de'
      ? `Blättere durch den kompletten Pool (${poolTaskCount} Cases). Pro Interview zieht die KI daraus ${poolTotal} ${poolTotal > 1 ? 'Cases' : 'Case'}; Level und Dauer passen sich an.`
      : `Browse the full pool (${poolTaskCount} cases). Per interview the AI draws ${poolTotal} ${poolTotal > 1 ? 'cases' : 'case'} from it; level and duration adapt.`,
    exampleTitle: lang === 'de' ? 'Beispiel-Interview' : 'Example interview',
    exampleHelp: poolTotal > 1
      ? (lang === 'de'
        ? `So könnte ein Interview aussehen: ${poolTotal} Cases aus verschiedenen Bereichen, nacheinander (plus kurzem Self-Intro am Anfang). "Andere Auswahl" zeigt eine andere Kombination.`
        : `How an interview could look: ${poolTotal} cases from distinct areas, back to back (plus a short self-intro at the start). "Reshuffle" shows another combination.`)
      : (lang === 'de'
        ? `So könnte ein Interview aussehen: 1 Case mit allen Schritten (plus kurzem Self-Intro am Anfang). "Andere Auswahl" zeigt einen anderen Case.`
        : `How an interview could look: 1 case with all steps (plus a short self-intro at the start). "Reshuffle" shows a different case.`),
    viewExample: lang === 'de' ? 'Beispiel' : 'Example',
    viewPool: `Pool (${poolTaskCount})`,
    caseOf: (i: number, n: number) =>
      lang === 'de' ? `Case ${i} von ${n}` : `Case ${i} of ${n}`,
    caseHeader: (k: number, n: number, area: string) => {
      const base = lang === 'de' ? `Case ${k} von ${n}` : `Case ${k} of ${n}`;
      return area ? `${base} · ${area}` : base;
    },
    browsePrev: lang === 'de' ? 'Vorheriger Case' : 'Previous case',
    browseNext: lang === 'de' ? 'Nächster Case' : 'Next case',
    scoringGoal:     lang === 'de' ? 'Scoring-Ziel' : 'Scoring goal',
    publicHint:      lang === 'de'
      ? 'Cevee-Standard-Template - kann nicht direkt bearbeitet werden. Nutze "Anpassen" um eine eigene Kopie zu erstellen, die du frei bearbeiten kannst.'
      : 'Cevee-curated template - read-only. Use "Customize" to create your own editable copy.',
    close:           lang === 'de' ? 'Schließen' : 'Close',
    useAsTemplate:   lang === 'de' ? 'Anpassen' : 'Customize',
    useAsTemplateLocked: lang === 'de'
      ? 'AVV erforderlich um eigene Templates anzulegen'
      : 'DPA required to create custom templates',
    invite:          lang === 'de' ? 'Einladen' : 'Invite',
    inviteLocked:    lang === 'de'
      ? 'AVV erforderlich um Kandidat:innen einzuladen'
      : 'DPA required to invite candidates',
    configureSimulation: lang === 'de' ? 'Simulation konfigurieren' : 'Configure simulation',
    none:            lang === 'de' ? '-' : '-',
    qBudget:         (sec: number) => sec >= 60
      ? `~${Math.round(sec / 60)} ${lang === 'de' ? 'Min.' : 'min'}`
      : `~${sec}s`,
  };

  return (
    <div
      className="fixed inset-0 z-[10010] flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-[0_24px_60px_-12px_rgba(15,23,42,0.25)] w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col border border-slate-200/70"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-7 py-5 border-b border-slate-200/60 gap-4">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold tracking-wider uppercase text-pink-600">
              {labels.badge}
              {active.is_cevee_official ? ` · Cevee Pre-Screen` : (active.is_public ? ` · Cevee Standard` : '')}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <h2 className="text-lg font-semibold text-slate-900 tracking-tight truncate">
                {cleanTitle}
              </h2>
              {isPool && (
                <span
                  className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md whitespace-nowrap bg-indigo-50 text-indigo-700 border border-indigo-100 flex-shrink-0"
                  title={lang === 'de'
                    ? 'Frage-Pool: KI wählt pro Session eine neue Auswahl'
                    : 'Question pool: AI picks a new subset each session'}
                >
                  {labels.poolBadge}
                </span>
              )}
            </div>
            {active.description && (
              <div className="mt-1">
                <p
                  className={[
                    'text-sm text-slate-500 leading-relaxed',
                    descExpanded ? '' : 'line-clamp-2',
                  ].join(' ')}
                >
                  {sanitizeInterviewCopy(active.description)}
                </p>
                {descTruncatable && (
                  <button
                    type="button"
                    onClick={() => setDescExpanded((v) => !v)}
                    className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-pink-600 hover:text-pink-700 transition-colors"
                  >
                    {descExpanded ? labels.readLess : labels.readMore}
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {langsAvailable.length > 1 && (
              <div className="inline-flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
                {langsAvailable.map((code) => (
                  <button
                    key={code}
                    onClick={() => setActiveLang(code)}
                    className={[
                      'px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors',
                      code === activeLang
                        ? 'bg-white text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.06)]'
                        : 'text-slate-500 hover:text-slate-700',
                    ].join(' ')}
                  >
                    {code}
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-7 py-6 overflow-y-auto space-y-7">
          {/* Stats row */}
          <div className={'grid gap-3 ' + (isSessionConfigurable && onInvite ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-4')}>
            <Stat
              icon={<Clock className="w-4 h-4 text-slate-400" />}
              label={labels.duration}
              value={
                isSessionConfigurable
                  ? (lang === 'de' ? '30 / 45 / 60 Min.' : '30 / 45 / 60 min')
                  : `${active.duration_minutes} ${labels.minutes}`
              }
              sub={
                isCeveeOfficial
                  ? (lang === 'de' ? 'standardisiert (fix)' : 'standardised (fixed)')
                  : isSessionConfigurable
                    ? (configAtInvite
                      ? (lang === 'de' ? 'in Einladung wählen' : 'set in invite')
                      : (lang === 'de' ? 'beim Start wählen' : 'chosen at start'))
                    : undefined
              }
            />
            <Stat
              icon={<MessageSquare className="w-4 h-4 text-slate-400" />}
              label={labels.category}
              value={active.type.replace('_', ' ')}
              capitalize
            />
            <Stat
              icon={
                persona && (
                  <div
                    className={`w-4 h-4 rounded-full bg-gradient-to-br ${persona.gradient}`}
                  />
                )
              }
              label={labels.interviewer}
              value={persona?.display_name || labels.none}
            />
            {isSessionConfigurable && !onInvite ? (
              <Stat
                icon={<ListChecks className="w-4 h-4 text-slate-400" />}
                label={labels.level}
                value={lang === 'de' ? 'Student / Junior / Mid / Senior' : 'Student / Junior / Mid / Senior'}
                sub={lang === 'de' ? 'beim Start wählen' : 'chosen at start'}
              />
            ) : (!isSessionConfigurable || isCeveeOfficial) && templateLevel ? (
              <Stat
                icon={<ListChecks className="w-4 h-4 text-slate-400" />}
                label={labels.level}
                value={templateLevel}
                capitalize
              />
            ) : null}
          </div>

          {/* Panels */}
          <section>
            <h3 className="font-semibold text-slate-900 text-sm mb-2">{labels.panelsTitle}</h3>
            {!active.enable_coding_panel && !active.enable_whiteboard_panel && !active.enable_numbers_panel && !active.enable_notepad_panel && (
              <p className="text-xs text-slate-500 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200/60">
                {labels.panelNone}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {active.enable_coding_panel && (
                <PanelPill icon={<Code className="w-4 h-4" />} label={labels.panelCoding} />
              )}
              {active.enable_whiteboard_panel && (
                <PanelPill icon={<PencilLine className="w-4 h-4" />} label={labels.panelWB} />
              )}
              {active.enable_numbers_panel && (
                <PanelPill icon={<Calculator className="w-4 h-4" />} label={labels.panelNum} />
              )}
              {active.enable_notepad_panel && (
                <PanelPill icon={<FileText className="w-4 h-4" />} label={labels.panelNotepad} />
              )}
            </div>
          </section>

          {/* Criteria */}
          {evalCriteria.length > 0 && (
            <section>
              <h3 className="font-semibold text-slate-900 text-sm mb-1">
                {labels.criteriaTitle}
              </h3>
              {!onUseAsTemplate && (
                <p className="text-xs text-slate-500 mb-2 leading-relaxed">
                  {labels.criteriaHelp}
                </p>
              )}
              <div className="rounded-2xl border border-slate-200/70 overflow-hidden bg-slate-50/30">
                <div className="grid grid-cols-[1fr_88px] gap-3 px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-400 border-b border-slate-200/60 bg-white/50">
                  <span>{labels.criteriaTitle}</span>
                  <span className="text-right">{labels.weightLabel}</span>
                </div>
                <div className="divide-y divide-slate-200/60">
                  {evalCriteria.map((c, i) => (
                    <div key={c.id || i} className="grid grid-cols-[1fr_88px] gap-3 px-3 py-2 items-start">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{sanitizeInterviewCopy(c.label)}</p>
                        {c.description && (
                          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                            {sanitizeInterviewCopy(c.description)}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-sm font-mono text-slate-700">
                        {(Number(c.weight) || 0).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-3 py-2 bg-white/60 border-t border-slate-200/60 flex items-center justify-end gap-2 text-xs">
                  <span className="text-slate-500">{labels.sum}:</span>
                  <span
                    className={[
                      'font-mono font-semibold',
                      Math.abs(totalWeight - 1) < 0.01
                        ? 'text-emerald-600'
                        : 'text-amber-600',
                    ].join(' ')}
                  >
                    {totalWeight.toFixed(2)}
                  </span>
                </div>
              </div>
            </section>
          )}

          {/* Questions (or Pool summary, when the template is a pool) */}
          {isPool ? (
            <section>
              <div className="mb-3 flex items-center gap-2">
                <h3 className="font-semibold text-slate-900 text-sm">{labels.poolTitle}</h3>
                <span className="text-[10px] uppercase tracking-wider font-bold text-pink-700 bg-pink-50 border border-pink-100 px-1.5 py-0.5 rounded-full">
                  {labels.poolBadge}
                </span>
              </div>
              <div className="rounded-2xl border border-slate-200/70 bg-gradient-to-br from-slate-50 to-white p-4 space-y-3">
                <PoolFact icon={<Shuffle className="w-4 h-4 text-pink-600" />} text={labels.poolPerSession} />
                {poolTaskCount > 0 && (
                  <PoolFact icon={<Layers className="w-4 h-4 text-slate-500" />} text={labels.poolSize} />
                )}
                <PoolFact icon={<ListChecks className="w-4 h-4 text-slate-500" />} text={labels.poolDistinct} />
                {!isCeveeOfficial && (
                  <PoolFact icon={<ListChecks className="w-4 h-4 text-slate-500" />} text={labels.poolDifficulty} />
                )}
                <PoolFact icon={<Clock className="w-4 h-4 text-slate-500" />} text={labels.poolAntiReplay} />
              </div>
              {poolAreas.length > 0 && (
                <div className="mt-3">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">
                    {labels.poolAreasTitle}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {poolAreas.map((area) => (
                      <span
                        key={area}
                        className="inline-flex items-center text-[11px] font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {showPoolSample && (
                <div className="mt-5">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                        {effectiveView === 'pool' ? labels.poolBrowseTitle : labels.exampleTitle}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">
                        {effectiveView === 'pool' ? labels.poolBrowseHelp : labels.exampleHelp}
                        {isRecruiterPoolPreview && !isPracticeOnlyPool && (
                          <span className="block mt-1 text-slate-400">
                            {labels.poolSampleRecruiterNote}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      {canBrowsePool && (
                        <div className="inline-flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
                          {(['example', 'pool'] as const).map((mode) => (
                            <button
                              key={mode}
                              type="button"
                              onClick={() => setPoolView(mode)}
                              className={[
                                'px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors',
                                effectiveView === mode
                                  ? 'bg-white text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.06)]'
                                  : 'text-slate-500 hover:text-slate-700',
                              ].join(' ')}
                            >
                              {mode === 'example' ? labels.viewExample : labels.viewPool}
                            </button>
                          ))}
                        </div>
                      )}
                      {effectiveView === 'pool' ? (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setBrowseIdx((i) => Math.max(0, i - 1))}
                            disabled={clampedIdx <= 0}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                            aria-label={labels.browsePrev}
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                          </button>
                          <span className="text-[11px] font-medium text-slate-500 tabular-nums whitespace-nowrap px-1">
                            {labels.caseOf(clampedIdx + 1, poolTaskCount)}
                          </span>
                          <button
                            type="button"
                            onClick={() => setBrowseIdx((i) => Math.min(poolTaskCount - 1, i + 1))}
                            disabled={clampedIdx >= poolTaskCount - 1}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                            aria-label={labels.browseNext}
                          >
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setSampleSeed(Math.floor(Math.random() * 1e9))}
                          className="inline-flex items-center gap-1 text-[11px] font-medium text-pink-700 hover:text-pink-800 bg-pink-50 hover:bg-pink-100 border border-pink-100 px-2 py-1 rounded-full transition"
                        >
                          <Shuffle className="w-3 h-3" />
                          {labels.poolSampleReshuffle}
                        </button>
                      )}
                    </div>
                  </div>
                  {effectiveView === 'pool' ? (
                    <ol className="space-y-2">
                      {(poolTasksAll[clampedIdx] || []).map((q, i) => (
                        <StepCard
                          key={q.id}
                          q={q}
                          i={i}
                          formatArea={formatArea}
                          showScoring={isRecruiterPoolPreview}
                          scoringLabel={labels.scoringGoal}
                          qBudget={labels.qBudget}
                        />
                      ))}
                    </ol>
                  ) : (
                    <div className="space-y-3">
                      {exampleInterviewCases.map((caseSteps, ci) => {
                        const areaName = poolAreaLabel(caseSteps[0]?.tags, formatArea);
                        return (
                          <div key={ci}>
                            {poolTotal > 1 && (
                              <p className="inline-flex text-[10px] uppercase tracking-wider font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded-full mb-1.5">
                                {labels.caseHeader(ci + 1, poolTotal, areaName)}
                              </p>
                            )}
                            <ol className="space-y-2">
                              {caseSteps.map((q, i) => (
                                <StepCard
                                  key={q.id}
                                  q={q}
                                  i={i}
                                  formatArea={formatArea}
                                  showScoring={isRecruiterPoolPreview}
                                  scoringLabel={labels.scoringGoal}
                                  qBudget={labels.qBudget}
                                />
                              ))}
                            </ol>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <p className="text-[11px] text-slate-400 mt-3 italic">
                {labels.poolHiddenNote}
              </p>
            </section>
          ) : (active.questions || []).length > 0 && (
            <section>
              <div className="mb-2">
                <h3 className="font-semibold text-slate-900 text-sm">{labels.questionsTitle}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{labels.questionsHelp}</p>
              </div>
              <ol className="space-y-2.5">
                {[...active.questions]
                  .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                  .map((q, i) => (
                    <li
                      key={q.id}
                      className="rounded-2xl border border-slate-200/70 bg-white p-3.5"
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5">
                          #{i + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-800 leading-snug">
                            {sanitizeInterviewCopy(q.question_text)}
                          </p>
                          {q.expected_answer_hints && (
                            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                              <span className="font-semibold uppercase tracking-wider text-[10px] text-slate-400 mr-1.5">
                                {labels.scoringGoal}:
                              </span>
                              {q.expected_answer_hints}
                            </p>
                          )}
                          {q.duration_seconds ? (
                            <p className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {labels.qBudget(q.duration_seconds)}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  ))}
              </ol>
            </section>
          )}

          {/* Read-only note */}
          {active.is_public && (
            <div className="flex items-start gap-2 p-3 rounded-2xl bg-slate-50 border border-slate-200/60">
              <ShieldCheck className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-slate-600 leading-relaxed">{labels.publicHint}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-7 py-4 border-t border-slate-200/60 bg-slate-50/60">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-semibold text-slate-700 rounded-full border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
          >
            {labels.close}
          </button>
          {onUseAsTemplate && (
            <button
              onClick={() => {
                let sampleSteps = poolSampleTaskSteps;
                if (!sampleSteps.length && isPool && (active.questions || []).length > 0) {
                  sampleSteps = pickSampleTaskSteps(active.questions || [], sampleSeed);
                }
                const langCode = active.language === 'en' ? 'en' : 'de';
                onUseAsTemplate(
                  active,
                  sampleSteps.length
                    ? { sampleSteps, language: langCode }
                    : { language: langCode },
                );
              }}
              disabled={!canUseAsTemplate}
              title={!canUseAsTemplate ? labels.useAsTemplateLocked : ''}
              className="px-5 py-2 text-sm font-semibold text-slate-700 rounded-full border border-slate-200 bg-white hover:bg-slate-50 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
            >
              {labels.useAsTemplate}
            </button>
          )}
          {onConfigureSimulation && sessionConfigurable && (
            <button
              type="button"
              onClick={() => onConfigureSimulation(active)}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 transition-colors"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {labels.configureSimulation}
            </button>
          )}
          {onInvite && (
            <button
              onClick={() => onInvite(active)}
              disabled={!canInvite}
              title={!canInvite ? labels.inviteLocked : ''}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-full bg-slate-900 hover:bg-pink-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
              {labels.invite}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  capitalize?: boolean;
}> = ({ icon, label, value, sub, capitalize }) => (
  <div className="rounded-2xl border border-slate-200/70 p-2.5 bg-white">
    <div className="flex items-center gap-1.5">
      {icon}
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
        {label}
      </p>
    </div>
    <p
      className={[
        'text-sm font-semibold text-slate-800 mt-0.5',
        capitalize ? 'capitalize' : '',
      ].join(' ')}
    >
      {value}
    </p>
    {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

/**
 * Human-readable area name for a pool step. Prefers the `arealabel:` tag
 * (carries the model's original text with real umlauts) and falls back to
 * prettifying the slug-based `area:` tag for older pools.
 */
const poolAreaLabel = (
  tags: InterviewQuestion['tags'] | undefined,
  formatArea: (a: string) => string,
): string => {
  const list = (tags || []).filter((t): t is string => typeof t === 'string');
  const labelTag = list.find((t) => t.startsWith('arealabel:'));
  if (labelTag) return labelTag.slice('arealabel:'.length);
  const areaTag = list.find((t) => t.startsWith('area:'));
  return areaTag ? formatArea(areaTag.slice('area:'.length)) : '';
};

const StepCard: React.FC<{
  q: InterviewQuestion;
  i: number;
  formatArea: (a: string) => string;
  /** Recruiter preview: show the internal scoring goal (hidden from candidates). */
  showScoring?: boolean;
  scoringLabel?: string;
  /** Formatter for the per-step time budget (e.g. "~3 min"). */
  qBudget?: (sec: number) => string;
}> = ({ q, i, formatArea, showScoring, scoringLabel, qBudget }) => (
  <li className="rounded-2xl border border-slate-200/70 bg-white p-3">
    <div className="flex items-start gap-2.5">
      <span className="text-[10px] uppercase tracking-wider font-bold text-pink-600 bg-pink-50 px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5">
        #{i + 1}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-slate-800 leading-snug">
          {sanitizeInterviewCopy(q.question_text)}
        </p>
        {showScoring && q.expected_answer_hints && (
          <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
            <span className="font-semibold uppercase tracking-wider text-[10px] text-slate-400 mr-1.5">
              {scoringLabel}:
            </span>
            {sanitizeInterviewCopy(q.expected_answer_hints)}
          </p>
        )}
        <div className="flex items-center gap-3 mt-1.5">
          {q.duration_seconds && qBudget ? (
            <span className="text-[11px] text-slate-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {qBudget(q.duration_seconds)}
            </span>
          ) : null}
          {poolAreaLabel(q.tags, formatArea) && (
            <span className="text-[10px] text-slate-400">
              {poolAreaLabel(q.tags, formatArea)}
            </span>
          )}
        </div>
      </div>
    </div>
  </li>
);

const PoolFact: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <div className="flex items-start gap-2.5">
    <span className="mt-0.5 flex-shrink-0">{icon}</span>
    <p className="text-sm text-slate-700 leading-snug">{text}</p>
  </div>
);

const PanelPill: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-pink-50/60 border border-pink-200/60 text-pink-800 text-sm font-medium">
    {icon}
    {label}
  </div>
);

export default TemplateDetailModal;
